import {Injectable} from '@angular/core';
import {firstValueFrom, map} from 'rxjs';
import {GetService} from "@shared/api/services/get.service";
import {UserLicenseHistoryEntry} from "@shared/api/models/query/user-license-history.model";
import {UserLicenseDeactivate, UserLicenseDeactivationReasonUpdate} from "@shared/api/models/command/user-license.model";

/**
 * UNIVAPLAT-2771: integration layer for the admin license deactivation endpoints
 * (`/api/v2/admin/users/{userId}/license*`, backend contract UNIVAPLAT-2756).
 *
 * All methods are Promise-based (firstValueFrom) per the codebase convention —
 * components must call these, never HttpClient directly. Error mapping
 * (404 unknown user, 409 no_active_license / license_already_active /
 * no_license_history, 422 deactivation_reason_too_long) is left to the callers'
 * catch blocks — the backend owns the user-facing error text.
 */
@Injectable({
    providedIn: 'root'
})
export class UserLicensesService extends GetService<UserLicenseHistoryEntry, UserLicenseHistoryEntry> {

    protected override get domainEntity() {
        return 'admin/users'
    };

    /**
     * `GET /api/v2/admin/users/{userId}/licenses`
     * Full license history for the user, newest-first.
     * Errors: 404 (unknown user).
     */
    async listHistory(userId: number): Promise<UserLicenseHistoryEntry[]> {
        const result = this.http.get<{ data: UserLicenseHistoryEntry[] }>(`${this.urlToEntity}/${userId}/licenses`)
            .pipe(
                map((response) => {
                    return response?.data ?? [];
                })
            )
        return firstValueFrom(result)
    }

    /**
     * `POST /api/v2/admin/users/{userId}/license/deactivate`
     * Deactivates the user's active license, optionally recording a reason
     * (max DEACTIVATION_REASON_MAX_LENGTH chars).
     * Errors: 404 (unknown user), 409 (no_active_license), 422 (deactivation_reason_too_long).
     */
    async deactivate(userId: number, reason?: string): Promise<{ id: number }> {
        const body: UserLicenseDeactivate = reason !== undefined ? {reason} : {};
        const result = this.http.post<{ data: { id: number } }>(`${this.urlToEntity}/${userId}/license/deactivate`, body)
            .pipe(
                map((response) => {
                    return response.data;
                })
            )
        return firstValueFrom(result)
    }

    /**
     * `POST /api/v2/admin/users/{userId}/license/reactivate`
     * Reactivates the user's most recently deactivated license. No body.
     * Errors: 404 (unknown user), 409 (license_already_active | no_license_history).
     */
    async reactivate(userId: number): Promise<{ id: number }> {
        const result = this.http.post<{ data: { id: number } }>(`${this.urlToEntity}/${userId}/license/reactivate`, {})
            .pipe(
                map((response) => {
                    return response.data;
                })
            )
        return firstValueFrom(result)
    }

    /**
     * `PATCH /api/v2/admin/users/{userId}/license/{licenseId}/deactivation-reason`
     * Updates the deactivation reason of a historical license period.
     * Errors: 404 (unknown user, or licenseId not owned by userId), 422 (deactivation_reason_too_long).
     */
    async updateDeactivationReason(userId: number, licenseId: number, reason: string): Promise<{ id: number }> {
        const body: UserLicenseDeactivationReasonUpdate = {reason};
        const result = this.http.patch<{ data: { id: number } }>(`${this.urlToEntity}/${userId}/license/${licenseId}/deactivation-reason`, body)
            .pipe(
                map((response) => {
                    return response.data;
                })
            )
        return firstValueFrom(result)
    }
}
